<template>
  <div>
    <DashboardCard
      :expanded="expanded"
      :widget-type="widgetType"
      title="Tasks"
      :loading="viewTypeModel === TasksViewTypeEnum.calendar ? loadingCalendarTasks : loadingTasks.tome || loadingTasks.team || loadingTasks.byme"
      :count="viewTypeModel === TasksViewTypeEnum.calendar ? filteredCalendarTasksCount : totalTasks"
      @update:expanded="$emit('update:expanded', $event)"
    >
      <template #actions>
        <!-- Sized on the group: Vuetify 3 stretches toggle buttons to the group's
             height (48px by default), where Vuetify 2 let a 36px button shrink the group. -->
        <v-btn-toggle
          v-model="viewTypeModel"
          mandatory
          class="mr-2"
          :style="{ height: '36px' }"
        >
          <v-btn icon :value="TasksViewTypeEnum.calendar">
            <v-icon>mdi-calendar-month-outline</v-icon>
          </v-btn>
          <v-btn icon :value="TasksViewTypeEnum.cards">
            <v-icon>mdi-cards-variant</v-icon>
          </v-btn>
        </v-btn-toggle>
        <v-btn data-track="task.create-form.open" @click="showTaskForm = true">
          <v-icon :start="$vuetify.display.smAndUp">mdi-plus</v-icon>
          <span v-if="$vuetify.display.smAndUp">Add Task</span>
        </v-btn>
      </template>
      <template #content>
        <template v-if="viewTypeModel === TasksViewTypeEnum.calendar">
          <v-row>
            <v-col>
              <!--       key need to rerender TasksCard when viewTypeModel changed   -->
              <TasksCard
                key="calendar-tasks"
                is-calendar-view
                :marketing="marketing"
                @update:count="filteredCalendarTasksCount = $event"
                @update:loading="loadingCalendarTasks = $event"
              />
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <v-row>
            <v-col>
              <!--       key need to rerender TasksCard when viewTypeModel changed   -->
              <TasksCard
                key="tasks-tome"
                :marketing="marketing"
                :task-privacy="TaskPrivacyEnum.me"
                @update:count="filteredTasksCount.tome = $event"
                @update:loading="loadingTasks.tome = $event"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <TasksCard
                :marketing="marketing"
                :task-privacy="TaskPrivacyEnum.my_team"
                @update:count="filteredTasksCount.team = $event"
                @update:loading="loadingTasks.team = $event"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <TasksCard
                :marketing="marketing"
                :task-privacy="TaskPrivacyEnum.by_me"
                @update:count="filteredTasksCount.byme = $event"
                @update:loading="loadingTasks.byme = $event"
              />
            </v-col>
          </v-row>
        </template>
      </template>
    </DashboardCard>
    <CreateTaskForm
      v-model="showTaskForm"
      :marketing="marketing"
      @submit="submitHandler"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapMutations, mapState } from 'vuex';

import { UserWidgetsEnum } from '@/bundles/Settings/enums/userLayoutEnum';
import { TaskPrivacyEnum } from '@/bundles/Tasks/enums/TaskPrivacyEnum';
import { TasksViewTypeEnum } from '@/bundles/Tasks/types';
import { SET_TASK_VIEW_TYPE } from '@/store/modules/task/types';

import TasksCard from '@/bundles/Tasks/components/TasksCard.vue';
import DashboardCard from '@/bundles/Dashboard/components/DashboardCard.vue';
import CreateTaskForm from '@/bundles/Tasks/components/CreateTaskForm.vue';

interface ITasks {
  filteredTasksCount: {
    tome: number;
    byme: number;
    team: number;
  };
  filteredCalendarTasksCount: number;
  loadingTasks: {
    tome: boolean;
    byme: boolean;
    team: boolean;
  };
  loadingCalendarTasks: boolean;
  showTaskForm: boolean;
  TasksViewTypeEnum?: typeof TasksViewTypeEnum;
  TaskPrivacyEnum?: typeof TaskPrivacyEnum;
}

export default defineComponent({
  name: 'Tasks',

  components: {
    CreateTaskForm,
    DashboardCard,
    TasksCard,
  },

  props: {
    expanded: Boolean,
    marketing: Boolean
  },

  emits: ['update:expanded'],

  data: (): ITasks => ({
    filteredTasksCount: {
      tome: 0,
      byme: 0,
      team: 0
    },
    filteredCalendarTasksCount: 0,
    loadingTasks: {
      tome: false,
      byme: false,
      team: false
    },
    loadingCalendarTasks: false,
    showTaskForm: false,
  }),

  computed: {
    ...mapState('Task', {
      viewType: (state: any) => state.viewType,
    }),
    widgetType (): UserWidgetsEnum {
      return UserWidgetsEnum.tasks;
    },
    totalTasks (): number {
      return this.filteredTasksCount.tome + this.filteredTasksCount.byme + this.filteredTasksCount.team;
    },
    viewTypeModel: {
      get (): TasksViewTypeEnum {
        return this.viewType;
      },
      set (value: TasksViewTypeEnum) {
        this.setViewType(value);
      }
    }
  },

  created () {
    this.TaskPrivacyEnum = TaskPrivacyEnum;
    this.TasksViewTypeEnum = TasksViewTypeEnum;
  },

  methods: {
    ...mapMutations('Task', {
      setViewType: SET_TASK_VIEW_TYPE,
    }),
    submitHandler () {
      this.$eventBus.$emit('fetch:tasks', { isPaginationChanged: false });
    }
  }
});
</script>
